import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Formik, Form as FormikForm } from "formik";
import { Grid, Message, Button, Loader, Progress } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { toast } from "components/Toast/Toast";
import { toast as toastify, Zoom } from "react-toastify";

import { FieldRow } from "components/form";
import { getUnusedDeliveryCode } from "services/apiMiddleware";
import FileInput from "components/FileInput/FileInput";
import { getAWSDate, getCountryCode, getDeliveryDeadline } from "../services/common";
import { CampaignAutomaticStatus, CampaignType, ResolutionType } from "models";
import { getNativeResolutions, getPrintSizes, getCampaigns } from "store/selectors";
import CSVSummary from "components/CSVSummary/CSVSummary";
import _ from "lodash";

const getFileContent = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsText(file);
  });
};

export default function CSVCampaignsForm(props) {
  const { onSuccess, goPrev } = props;
  const [hasValue, setHasValue] = useState("");
  const [areCampaignsLoaded, setAreCampaignsLoaded] = useState(false);
  const dispatch = useDispatch();
  const { isCsvDataLoading, csvDataItems } = useSelector(getCampaigns);

  const { t } = useTranslation("common");
  const progressToastId = "progressUpdate";
  const finalSummaryToastId = "finalSummary";
  let finalSummary = [];

  const notifyProgress = () =>
    toastify(progressToastId, {
      render: <p>Initializing...</p>,
      autoClose: false,
      closeButton: true,
      position: "bottom-right",
      toastId: progressToastId,
    });
  const handleReportUpdate = (campaignName, errorType, errorText) => {
    finalSummary = [
      ...finalSummary,
      {
        campaignName: campaignName,
        errorType: errorType,
        errorText: errorText,
      },
    ];
  };

  useEffect(() => {
    dispatch.nativeResolutions.load();
    dispatch.printSizes.load();
    if (!isCsvDataLoading && !areCampaignsLoaded) {
      dispatch.campaigns.loadCsvActiveItems();
    }
    !isCsvDataLoading && setAreCampaignsLoaded(true);
  }, [isCsvDataLoading, areCampaignsLoaded]);

  let txt = "";
  let digitalSizes = useSelector(getNativeResolutions);
  let printSizes = useSelector(getPrintSizes);

  printSizes = printSizes && printSizes.items;
  digitalSizes = digitalSizes && digitalSizes.items;
  const formatDate = (date) => {
    // Campaign start date should follow DD.MM.YYYY format
    const campaignStartDate = date.split(".");
    // Date day value formatting if needed to be 2 digits if value is less than 10
    campaignStartDate[0] =
      parseInt(campaignStartDate[0]) < 10
        ? `0${parseInt(campaignStartDate[0])}`
        : campaignStartDate[0];
    // Date month value formatting if needed to be 2 digits if value is less than 10
    campaignStartDate[1] =
      parseInt(campaignStartDate[1]) < 10
        ? `0${parseInt(campaignStartDate[1])}`
        : campaignStartDate[1];
    // Returns date in ISO format YYYY-MM-DD to be parsed
    return campaignStartDate.reverse().join("-");
  };
  const submit = async (values, actions) => {
    notifyProgress();
    let campaignName = "";
    let campaignLine = "";
    try {
      const lines = values.content.split("\n");
      let successfullyCreatedCampaigns = lines.length;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line || line === "\n" || line.trim().length === 0) {
          successfullyCreatedCampaigns -= 1;
          continue;
        }
        const [
          name,
          advertiser,
          type,
          start_time,
          end_time,
          campaignResolutions,
          orderlineID,
          salesperson,
          salesperson_contact,
          mediaDeliveryPersonContacts,
          adminNotes,
        ] = line.split(";");

        campaignName = name;
        campaignLine = i + 1;
        const imageExtensions = ["jpg", "png"];

        const newCampaignType =
          type.toLowerCase().indexOf(CampaignType.DIGITAL.toLowerCase()) > -1 ? "digital" : "print";
        const campaignResolutionType =
          newCampaignType === "digital" ? "nativeResolutions" : "printSizes";
        const checkItemResolutionMatches = (campaignData, newItemResolution) => {
          let newItemResolutionValues = [];
          let campaignResolutionValues = [];
          if (
            (newItemResolution === "" && campaignData[campaignResolutionType].items.length > 0) ||
            (newItemResolution !== "" && campaignData[campaignResolutionType].items.length <= 0)
          ) {
            return false;
          }
          if (newItemResolution !== "" && campaignData[campaignResolutionType].items.length >= 1) {
            newItemResolutionValues =
              newItemResolution.indexOf(",") > -1
                ? newItemResolution.split(",")
                : [newItemResolution];

            newItemResolutionValues = newItemResolutionValues.map((value) => ({
              ...(newCampaignType === "digital"
                ? {
                    value: value.split("_")[0] || value,
                    type: imageExtensions.some((ext) => value.includes(ext))
                      ? ResolutionType.IMAGE
                      : ResolutionType.IMAGE_AND_VIDEO,
                  }
                : { name: value.split("_")[0] || value }),
            }));

            const nonExistantResolutions = newItemResolutionValues.filter(
              (campaignResolution, i) => {
                const occurrences =
                  newCampaignType === "digital"
                    ? _.find(digitalSizes, {
                        value: campaignResolution.value,
                        type: campaignResolution.type,
                      })
                    : _.find(printSizes, {
                        name: campaignResolution.name,
                      });
                return !occurrences;
              }
            );

            if (nonExistantResolutions.length > 0) {
              newItemResolutionValues = _.differenceWith(
                newItemResolutionValues,
                nonExistantResolutions,
                _.isEqual
              );
            }

            campaignResolutionValues = campaignData[campaignResolutionType].items.map((item) => {
              return {
                ...(newCampaignType === "digital"
                  ? item &&
                    item.nativeResolution && {
                      value: item.nativeResolution.value,
                      type: item.nativeResolution.type,
                    }
                  : item && item.printSize && { name: item.printSize.name }),
              };
            });
          }
          const resolutionValuesMatches = _.intersectionWith(
            campaignResolutionValues,
            newItemResolutionValues,
            _.isEqual
          );
          return (
            campaignResolutionValues.length === newItemResolutionValues.length &&
            newItemResolutionValues.length === resolutionValuesMatches.length
          );
        };

        const isCampaignDuplicated = () => {
          return csvDataItems.filter(
            (item) =>
              checkItemResolutionMatches(item, campaignResolutions) &&
              item.orderlineID &&
              item.orderlineID === orderlineID &&
              item.start_time === formatDate(start_time) &&
              item.end_time === formatDate(end_time)
          );
        };
        if (isCampaignDuplicated().length >= 1) {
          successfullyCreatedCampaigns -= 1;
          toastify.update(progressToastId, {
            render: `Duplicated campaign ${campaignName} found in document line ${campaignLine}`,
            type: "error",
          });
          handleReportUpdate(
            name,
            "error",
            `Duplicated campaign ${campaignName} found in document line ${campaignLine}`
          );
          continue;
        }

        toastify.update(progressToastId, {
          // disabling eslint here because it messes up with valid syntax unnecessarily.
          /* eslint-disable */
          render:
          <>
              <h4>{t("CSV_campaign_creation_progress")}</h4>
              <p>
                <Loader active inline size="small" style={{ marginRight: "10px" }} />
                {t("CSV_progress_message",{campaignName:campaignName,campaignLine:campaignLine, totalCampaigns:successfullyCreatedCampaigns})}
              </p>
              <Progress percent={Math.round((campaignLine*100)/successfullyCreatedCampaigns)} size="small" inverted progress success/>
          </>
        });
        /* eslint-enable */
        let requiredResolutions = [];
        let nativeResolutions = [];
        let printResolutions = [];
        const createProps = {
          name,
          advertiser,
          type: CampaignType[newCampaignType.toUpperCase()],
          start_time: formatDate(start_time),
          end_time: formatDate(end_time),
          orderlineID: orderlineID || null,
          mediaDeliveryPersonContacts: mediaDeliveryPersonContacts.split(","),
          salesperson: salesperson || null,
          salesperson_contact: salesperson_contact || null,
          adminNotes: adminNotes || null,
          scheduling_notes:
            getCountryCode() === "fi" ? t("default_scheduling_note_finnish_country_code") : null,
          status: CampaignAutomaticStatus.AWAITING_DELIVERY,
          statusGroup: "ACTIVE",
          automaticStatus: CampaignAutomaticStatus.AWAITING_DELIVERY,
          contract_number: await getUnusedDeliveryCode(),
          deliveryDeadline: getAWSDate(
            getDeliveryDeadline(formatDate(start_time), CampaignType[newCampaignType.toUpperCase()])
          ),
        };
        const checkResolutionExists = (sizesToCheck) => {
          let campaignSizes = String(sizesToCheck)
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length);

          if (
            createProps &&
            createProps.type &&
            createProps.type.toLowerCase().includes(CampaignType.DIGITAL.toLowerCase())
          ) {
            campaignSizes = campaignSizes.map((raw) => {
              const [value, ext] = raw.split("_");
              const normalizedValue = value.trim();
              const isImage = imageExtensions.some((e) =>
                String(ext).toLowerCase().includes(e.toLowerCase())
              );
              return {
                value: normalizedValue,
                type: isImage ? ResolutionType.IMAGE : ResolutionType.IMAGE_AND_VIDEO,
              };
            });

            campaignSizes.forEach(({ value, type }, i) => {
              const found = digitalSizes.find((ds) => ds.value === value && ds.type === type);
              if (!found) {
                handleReportUpdate(
                  name,
                  "warning",
                  t("CSV_error_resolution_not_found", {
                    resolutionValue: value,
                    resolutionType:
                      type === ResolutionType.IMAGE_AND_VIDEO ? "mp4\\mov\\jpg\\png" : "jpg\\png",
                    campaignName: name,
                    errorLine: i + 1,
                  })
                );
              } else {
                nativeResolutions.push(found.id);
                requiredResolutions.push(found.requiredResolutionID);
              }
            });
          } else {
            campaignSizes.forEach((raw, i) => {
              const val = raw.trim();
              const found = printSizes.find((ps) => ps.name === val);
              if (!found) {
                handleReportUpdate(
                  name,
                  "warning",
                  t("CSV_error_resolution_not_found", {
                    resolutionValue: val,
                    resolutionType: "print",
                    campaignName: name,
                    errorLine: i + 1,
                  })
                );
              } else {
                printResolutions.push(found.id);
              }
            });
          }
        };
        if (type && campaignResolutions) {
          checkResolutionExists(campaignResolutions);
        }
        // Check for errors
        if (new Date(createProps.start_time) > new Date(createProps.end_time)) {
          handleReportUpdate(
            name,
            "error",
            t("CSV_end_date_before_start_date_error", { errorLine: i + 1 })
          );
          successfullyCreatedCampaigns = successfullyCreatedCampaigns -= 1;
        } else if (
          !createProps.type ||
          !createProps.name ||
          !createProps.start_time ||
          !createProps.end_time ||
          !createProps.advertiser
        ) {
          handleReportUpdate(
            name,
            "error",
            t("CSV_error_mandatory_fields", { line: i + 1, campaignName: name })
          );
          successfullyCreatedCampaigns = successfullyCreatedCampaigns -= 1;
        } else {
          requiredResolutions = [...new Set(requiredResolutions)];
          await dispatch.campaigns.create({
            createProps: createProps,
            requiredResolutions,
            nativeResolutions,
            printSizes: printResolutions.length > 0 ? printResolutions : undefined,
          });
        }
      }
      if (successfullyCreatedCampaigns > 0) {
        dispatch.campaigns.loadActive(true);
        toastify.update(progressToastId, {
          render: t("CSV_campaigns_created_successfully", {
            count: successfullyCreatedCampaigns,
            campaignAmount:
              successfullyCreatedCampaigns > 1
                ? t("CSV_campaign_plural")
                : t("CSV_campaign_singular"),
          }),
          autoClose: 8000,
        });
      }
      if (successfullyCreatedCampaigns <= 0) {
        toastify.update(progressToastId, {
          type: "error",
          render: <p> No campaigns created</p>,
          autoClose: 8000,
        });
      }

      finalSummary.length >= 1 &&
        toastify(<CSVSummary finalSummary={finalSummary} t={t} />, {
          autoClose: false,
          closeButton: true,
          position: "top-left",
          transition: Zoom,
          draggable: false,
          toastClassName: "finalSummaryToast",
          toastId: finalSummaryToastId,
          style: { width: "100%", maxHeight: window.innerHeight - 40 },
        });
      onSuccess();
    } catch (error) {
      console.error(error);
      toast("error", t("CSV_error_campaign_creation_failed") + error.message);
    }
  };

  const handleValueChange = (e) => {
    setHasValue(e.content);
  };

  return (
    <Formik initialValues={{}} onSubmit={submit}>
      {({ isSubmitting, setFieldValue, values }) => (
        <FormikForm>
          <h2>{t("create_campaigns_from_CSV")}</h2>
          <Message
            header={`${t("CSV_format")}:`}
            content={
              <>
                <p style={{ fontWeight: "bold", wordBreak: "break-word", letterSpacing: ".85px" }}>
                  {`${t("CSV_campaign_name_instructions")};${t("CSV_advertiser_name")};${t(
                    "CSV_campaign_type"
                  )};${t("CSV_campaign_start_date")}(${t("CSV_date_format")});${t(
                    "CSV_campaign_end_date"
                  )}(${t("CSV_date_format")});resolution1,resolution2,resolution3;Orderline ID;
                  ${t("CSV_salesperson_name")};${t("CSV_salesperson_email")};${t(
                    "CSV_media_delivery_person_email"
                  )};${t("campaign_adminNotes")}`}
                </p>
                <div className="header">{t("CSV_digital_campaign_example")}:</div>
                <p style={{ fontWeight: "bold", wordBreak: "break-all", letterSpacing: ".85px" }}>
                  Digital campaign name example;Advertiser name
                  example;Digital;13.12.2022;19.12.2022;1080x1920_jpg,1000x1000,1080x1920,1312x952,1080x1024,505x505,111x111_png,111x111;688287;Salesperson;Salesperson@email.com;John.Doe@email.fi;
                </p>
                <div className="header">{t("CSV_print_campaign_example")}:</div>
                <p style={{ fontWeight: "bold", wordBreak: "break-all", letterSpacing: ".85px" }}>
                  Print campaign name example;Advertiser name
                  example;Print;13.12.2022;19.12.2022;BIGLIGHT,SUURTAULUT,SUURTAULUT2,ADSHEL,ADSHEL-PILARIT,PILARIT;688287;Salesperson;Salesperson@email.com;John.Doe@email.fi;
                </p>
              </>
            }
          />
          <br />
          {!areCampaignsLoaded || csvDataItems.length === 0 ? (
            <Loader active inline="centered">
              {t("loading")}
            </Loader>
          ) : (
            <>
              <Grid columns={2} verticalAlign="middle">
                <FileInput
                  onChange={async (newFiles) => {
                    txt = await getFileContent(newFiles[0]);
                    setFieldValue("content", txt);
                  }}
                  accept="text/csv, application/vnd.ms-excel"
                  multiple={false}
                  // onError={...} // TODO implement
                />
              </Grid>
              <Grid columns={2} verticalAlign="middle">
                <FieldRow
                  label={t("CSV_content")}
                  name="content"
                  type="area"
                  onChange={handleValueChange(values)}
                />
              </Grid>
            </>
          )}
          <div className="form-footer">
            {goPrev && (
              <Button type="button" primary onClick={goPrev}>
                {t("CSV_back_button")}
              </Button>
            )}
            <Button
              primary
              type="submit"
              loading={isSubmitting}
              disabled={!areCampaignsLoaded || !hasValue}
            >
              {t("CSV_next_button")}
            </Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}
